import {
  ROUTE_CERTIFICATION_NEW,
  ROUTE_COMPLETE_PROFILE,
  ROUTE_CRAFTING_VIDEO,
  ROUTE_EDUCATION_NEW,
  ROUTE_EXPERIENCE_NEW,
  ROUTE_LOOKING_FOR,
  ROUTE_PROFILER,
  ROUTE_SKILLSET_NEW,
  ROUTE_UPLOAD_PIC,
  ROUTE_WHAT_YOUR_NAME,
} from "../constants/routes";

export type MissingProfileSection = {
  key: string;
  label: string;
  route?: string;
};

export type ApplyBlockedPayload = {
  message?: string;
  missingSections: MissingProfileSection[];
};

const ROUTE_BY_KEY: Record<string, string> = {
  skills: ROUTE_SKILLSET_NEW,
  skill: ROUTE_SKILLSET_NEW,
  video: ROUTE_CRAFTING_VIDEO,
  video_cv: ROUTE_CRAFTING_VIDEO,
  videocv: ROUTE_CRAFTING_VIDEO,
  education: ROUTE_EDUCATION_NEW,
  experience: ROUTE_EXPERIENCE_NEW,
  certification: ROUTE_CERTIFICATION_NEW,
  certifications: ROUTE_CERTIFICATION_NEW,
  certificate: ROUTE_CERTIFICATION_NEW,
  certificates: ROUTE_CERTIFICATION_NEW,
  preferences: ROUTE_LOOKING_FOR,
  looking_for: ROUTE_LOOKING_FOR,
  preference: ROUTE_LOOKING_FOR,
  profiler: ROUTE_PROFILER,
  personality: ROUTE_PROFILER,
  exam: ROUTE_PROFILER,
  photo: ROUTE_UPLOAD_PIC,
  profile_picture: ROUTE_UPLOAD_PIC,
  profile_photo: ROUTE_UPLOAD_PIC,
  name: ROUTE_WHAT_YOUR_NAME,
  demographics: `${ROUTE_UPLOAD_PIC}?edit`,
};

const ROUTE_BY_BACKEND_PATH: Record<string, string> = {
  "/profile/skills": ROUTE_SKILLSET_NEW,
  "/profile/skill": ROUTE_SKILLSET_NEW,
  "/profile/video": ROUTE_CRAFTING_VIDEO,
  "/profile/video-cv": ROUTE_CRAFTING_VIDEO,
  "/profile/education": ROUTE_EDUCATION_NEW,
  "/profile/experience": ROUTE_EXPERIENCE_NEW,
  "/profile/certification": ROUTE_CERTIFICATION_NEW,
  "/profile/certifications": ROUTE_CERTIFICATION_NEW,
  "/profile/preferences": ROUTE_LOOKING_FOR,
  "/profile/looking-for": ROUTE_LOOKING_FOR,
  "/profile/profiler": ROUTE_PROFILER,
  "/profile/personality": ROUTE_PROFILER,
  "/profile/photo": ROUTE_UPLOAD_PIC,
  "/profile/name": ROUTE_WHAT_YOUR_NAME,
  "/profile/demographics": `${ROUTE_UPLOAD_PIC}?edit`,
};

const LABEL_BY_KEY: Record<string, string> = {
  skills: "Skills",
  skill: "Skills",
  video: "Video CV",
  video_cv: "Video CV",
  education: "Education",
  experience: "Experience",
  certification: "Certifications",
  certifications: "Certifications",
  preferences: "Job preferences",
  looking_for: "Job preferences",
  profiler: "Personality profiler",
  personality: "Personality profiler",
  exam: "Personality profiler",
  photo: "Profile photo",
  name: "Name",
  demographics: "Demographics",
};

export type DemographicsInput = {
  dob?: string | null;
  gender?: string | null;
  languages?: string[] | null;
  city?: unknown;
};

function normalizeCityValue(city: unknown): string {
  if (!city) return "";
  if (typeof city === "string") return city.trim();
  if (typeof city === "object" && city !== null) {
    const obj = city as { value?: string; label?: string };
    return String(obj.value ?? obj.label ?? "").trim();
  }
  return "";
}

function normalizeLanguages(languages: unknown): string[] {
  if (!Array.isArray(languages)) return [];
  return languages
    .map((lang) => {
      if (typeof lang === "string") return lang.trim();
      if (lang && typeof lang === "object") {
        const obj = lang as { value?: string; label?: string };
        return String(obj.value ?? obj.label ?? "").trim();
      }
      return "";
    })
    .filter(Boolean);
}

function coalesceLanguages(...sources: unknown[]): string[] | undefined {
  for (const source of sources) {
    const normalized = normalizeLanguages(source);
    if (normalized.length > 0) return normalized;
  }
  return undefined;
}

function coalesceDemographicValue(...values: unknown[]): string | null {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (text.length > 0) return text;
  }
  return null;
}

export function isDemographicsComplete(input: DemographicsInput): boolean {
  const languages = normalizeLanguages(input.languages);
  return !!(
    coalesceDemographicValue(input.dob) &&
    coalesceDemographicValue(input.gender) &&
    languages.length > 0 &&
    normalizeCityValue(input.city)
  );
}

export function getMissingDemographicsSections(input: DemographicsInput): MissingProfileSection[] {
  if (isDemographicsComplete(input)) return [];
  return [{ key: "demographics", label: "Demographics", route: `${ROUTE_UPLOAD_PIC}?edit` }];
}

const DEMOGRAPHICS_SECTION: MissingProfileSection = {
  key: "demographics",
  label: "Demographics",
  route: `${ROUTE_UPLOAD_PIC}?edit`,
};

export function isDemographicsSection(section: MissingProfileSection): boolean {
  const key = section.key?.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "") ?? "";
  const label = section.label?.toLowerCase().trim() ?? "";
  const route = section.route?.toLowerCase().trim() ?? "";

  if (key.includes("demographic") || label === "demographics" || label === "profile details") {
    return true;
  }

  return route.includes("/demographics");
}

function normalizeSectionDedupeKey(section: MissingProfileSection): string {
  if (isDemographicsSection(section)) return "demographics";

  const key = section.key?.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "") ?? "";
  if (key) return key;

  return section.label?.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "") ?? "";
}

function dedupeMissingSections(sections: MissingProfileSection[]): MissingProfileSection[] {
  const seen = new Set<string>();
  const result: MissingProfileSection[] = [];

  for (const section of sections) {
    const id = normalizeSectionDedupeKey(section);
    if (seen.has(id)) continue;
    seen.add(id);

    if (id === "demographics") {
      result.push({
        ...DEMOGRAPHICS_SECTION,
        route: section.route || DEMOGRAPHICS_SECTION.route,
      });
    } else {
      result.push(section);
    }
  }

  return result;
}

export function getDemographicsInputFromAppState(state: any): DemographicsInput {
  const user = state?.app?.user?.user || {};
  const profile = state?.app?.userProfile || {};

  // Demographics are saved via user/detail/update — prefer user detail over profile/read.
  return {
    dob: coalesceDemographicValue(user.dob, profile.dob),
    gender: coalesceDemographicValue(user.gender, profile.gender),
    languages: coalesceLanguages(user.languages, profile.languages),
    city: normalizeCityValue(user.city) || normalizeCityValue(profile.city) || null,
  };
}

export function mergeApplyBlockedWithDemographics(
  apiPayload: ApplyBlockedPayload | null,
  demographicsInput: DemographicsInput
): ApplyBlockedPayload | null {
  const apiSections = apiPayload?.missingSections ?? [];
  const apiAlreadyHasDemographics = apiSections.some(isDemographicsSection);
  const demographicSections = apiAlreadyHasDemographics
    ? []
    : getMissingDemographicsSections(demographicsInput);
  const merged = dedupeMissingSections([...demographicSections, ...apiSections]);

  if (merged.length === 0) return null;

  return {
    message:
      apiPayload?.message && apiSections.length > 0
        ? apiPayload.message
        : buildApplyBlockedSummary(merged),
    missingSections: merged,
  };
}

export function resolveMissingSectionRoute(section: MissingProfileSection): string {
  const backendRoute = section.route?.trim();
  if (backendRoute && ROUTE_BY_BACKEND_PATH[backendRoute]) {
    return ROUTE_BY_BACKEND_PATH[backendRoute];
  }
  const key = section.key?.toLowerCase().replace(/\s+/g, "_");
  if (key && ROUTE_BY_KEY[key]) {
    return ROUTE_BY_KEY[key];
  }
  return ROUTE_COMPLETE_PROFILE;
}

export function normalizeMissingSections(raw: unknown): MissingProfileSection[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .filter((item) => item && typeof item === "object")
    .map((item: any) => ({
      key: String(item.key ?? item.section ?? "").trim(),
      label: String(item.label ?? item.name ?? item.key ?? "Profile section").trim(),
      route: item.route ? String(item.route).trim() : undefined,
    }))
    .filter((item) => item.label.length > 0);
}

function extractMissingSectionsFromPayload(payload: any): MissingProfileSection[] {
  if (!payload || typeof payload !== "object") return [];

  const direct = normalizeMissingSections(
    payload.missingSections ??
      payload.missing_sections ??
      payload.missingProfileSections ??
      payload.missing_profile_sections
  );
  if (direct.length > 0) return direct;

  const nested = normalizeMissingSections(
    payload.result?.missingSections ??
      payload.result?.missing_sections ??
      payload.data?.missingSections ??
      payload.data?.missing_sections
  );
  if (nested.length > 0) return nested;

  return sectionsFromProfileStatus(payload.profileStatus ?? payload.result?.profileStatus);
}

function sectionsFromProfileStatus(profileStatus: any): MissingProfileSection[] {
  if (!profileStatus || typeof profileStatus !== "object") return [];

  const checks: Array<{ key: string; statusKey: string }> = [
    { key: "skills", statusKey: "skillStatus" },
    { key: "video", statusKey: "videoStatus" },
    { key: "education", statusKey: "educationStatus" },
    { key: "experience", statusKey: "experienceStatus" },
    { key: "certification", statusKey: "certificateStatus" },
    { key: "preferences", statusKey: "preferenceStatus" },
    { key: "profiler", statusKey: "examStatus" },
    { key: "photo", statusKey: "profileStatus" },
    { key: "name", statusKey: "profileStatus" },
  ];

  return checks
    .filter(({ statusKey }) => {
      const value = profileStatus[statusKey];
      return value === "INACTIVE" || value === false;
    })
    .map(({ key }) => ({
      key,
      label: LABEL_BY_KEY[key] ?? key,
    }));
}

/** Parse "Skills, Video CV" from API message text. */
export function parseSectionsFromApplyMessage(message: string): MissingProfileSection[] {
  if (!message || typeof message !== "string") return [];

  const patterns = [
    /before applying:\s*(.+)$/i,
    /following sections?:\s*(.+)$/i,
    /complete the following:\s*(.+)$/i,
    /missing sections?:\s*(.+)$/i,
  ];

  for (const pattern of patterns) {
    const match = message.match(pattern);
    if (!match?.[1]) continue;

    return match[1]
      .split(/,|\band\b/i)
      .map((part) => part.trim())
      .filter((label) => label.length > 0)
      .map((label) => {
        const key = label.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "");
        return { key: key || label, label };
      });
  }

  return [];
}

export function buildApplyBlockedSummary(sections: MissingProfileSection[]): string {
  const labels = sections.map((s) => s.label).filter(Boolean);
  if (labels.length === 0) {
    return "Please complete the required profile sections before applying.";
  }
  if (labels.length === 1) {
    return `Please complete ${labels[0]} before applying for this job.`;
  }
  if (labels.length === 2) {
    return `Please complete ${labels[0]} and ${labels[1]} before applying for this job.`;
  }
  const last = labels[labels.length - 1];
  const rest = labels.slice(0, -1).join(", ");
  return `Please complete ${rest}, and ${last} before applying for this job.`;
}

function resolveErrorMessage(err: any): string {
  const candidates = [
    err?.errorMsg,
    err?.message,
    err?.rawResponse?.message,
    err?.rawResponse?.result?.message,
    err?.response?.data?.message,
    err?.data?.message,
  ];
  for (const msg of candidates) {
    if (typeof msg === "string" && msg.trim()) return msg.trim();
  }
  return "";
}

export function parseApplyBlockedError(err: any): ApplyBlockedPayload | null {
  const payloads = [err?.rawResponse, err, err?.response?.data, err?.data].filter(Boolean);

  for (const payload of payloads) {
    const extracted = extractApplyBlockedFromApiResponse(payload);
    if (extracted) return extracted;
  }

  const message = resolveErrorMessage(err);
  const sections = parseSectionsFromApplyMessage(message);
  if (sections.length === 0) return null;

  return {
    message: message || buildApplyBlockedSummary(sections),
    missingSections: dedupeMissingSections(sections),
  };
}

export function extractApplyBlockedFromApiResponse(response: any): ApplyBlockedPayload | null {
  if (!response || typeof response !== "object") return null;

  const sections = extractMissingSectionsFromPayload(response);
  const message =
    (typeof response.message === "string" && response.message.trim()) ||
    (typeof response.result?.message === "string" && response.result.message.trim()) ||
    "";

  const resolvedSections =
    sections.length > 0 ? sections : parseSectionsFromApplyMessage(message);

  if (resolvedSections.length === 0) return null;

  return {
    message: message || buildApplyBlockedSummary(resolvedSections),
    missingSections: dedupeMissingSections(resolvedSections),
  };
}
